/**
 * freebird-collection-card.js — DEV-163
 *
 * Makes the swatch row on a Freebird collection card operable. Clicking or
 * keying a swatch re-dresses that card in place — photograph, price,
 * colourway line, selected ring, sold-out treatment — and repoints its link
 * so the product page opens on that colourway.
 *
 * ONE DELEGATED LISTENER, NOT A CUSTOM ELEMENT. Every value the interaction
 * needs already lives in DOM attributes and classes, so there is no per-card
 * state for an element instance to hold; a custom element would be a
 * connectedCallback wrapper around logic that needs no instance. Delegation
 * also means a card injected later — DEV-169's load-more will do exactly
 * that — needs no registration. (Custom elements auto-upgrade too, so that
 * second point is a convenience, not the reason.)
 *
 * Delegating from `document` is safe alongside base Horizon's own card
 * markup because the class names are namespaced: `closest('.fb-collection-
 * card')` returns null for a click on a base `.product-card`, and the
 * handler exits before doing anything. The styleguide also renders
 * standalone interactive swatches with no card ancestor; those resolve to
 * the same no-op rather than an error.
 *
 * NO IMPORTS. A relative import between two files in `assets/` resolves but
 * skips the `?v=` cache-busting hash `asset_url` appends, risking a stale
 * cached copy. Anything shared goes through `window.Freebird`, as
 * `freebird-cart-sync.js` does.
 */

(() => {
  const CARD = '.fb-collection-card';
  const SWATCH = '.fb-swatch-compact--interactive';
  const ROW = '.fb-swatch-row';

  /**
   * The card's own candidate ladder, and it must stay in step with the
   * `widths:` list in snippets/freebird-collection-card.liquid. Liquid cannot
   * read this file and this file cannot read Liquid, so these six numbers are
   * a second copy — if the card's ladder moves, this moves with it.
   */
  const LADDER = [160, 280, 380, 580, 760, 1160];

  /**
   * The row renders six swatches and hides the fourth through sixth below
   * 750px, so the set the keyboard may enter is viewport-dependent while the
   * markup is not. 750 is the theme's real breakpoint; 768 would open an 18px
   * band where the row is mobile and this is not.
   */
  const MOBILE = window.matchMedia('(max-width: 749px)');

  /** Swatches a shopper can actually reach at the current viewport. */
  const visibleSwatches = (row) =>
    Array.from(row.querySelectorAll(SWATCH)).filter((el) => el.offsetParent !== null);

  /**
   * Rebuilds a srcset from one CDN url, capped at the source image's real
   * width.
   *
   * Shopify never upscales: asking for a rung wider than the file returns the
   * smaller file while the srcset label still claims the rung, and the
   * browser believes the label — so it picks that candidate and paints soft,
   * with no error anywhere. Capping is the whole reason the swatch carries
   * `data-fb-photo-width`.
   *
   * `sizes` is deliberately untouched. The slot's geometry does not change
   * when the photograph does; only which colourway fills it.
   */
  const buildSrcset = (rawUrl, nativeWidth) => {
    let url;
    try {
      url = new URL(rawUrl, window.location.href);
    } catch {
      return null;
    }

    const cap = Number.parseInt(nativeWidth, 10);
    let rungs = Number.isFinite(cap) && cap > 0 ? LADDER.filter((w) => w <= cap) : LADDER.slice();

    // A source narrower than the smallest rung still deserves one honest
    // candidate — its own width — rather than an empty srcset.
    if (!rungs.length) rungs = [Number.isFinite(cap) && cap > 0 ? cap : LADDER[0]];

    const candidates = rungs.map((w) => {
      url.searchParams.set('width', String(w));
      return `${url.href} ${w}w`;
    });

    url.searchParams.set('width', String(rungs[rungs.length - 1]));
    return { srcset: candidates.join(', '), src: url.href };
  };

  /**
   * Sets which swatch is chosen, for every channel at once.
   *
   * Selected class, `aria-pressed` and the roving tabindex are one concept
   * and move as one write. Splitting them lets the mouse path move the ring
   * while the keyboard path moves the tab stop, so clicking the fourth swatch
   * and pressing Tab returns focus to the first — a swatch that is no longer
   * the chosen one.
   *
   * The tab stop must land on a VISIBLE swatch. Below 750px the fourth
   * through sixth are `display: none`, and a tabindex="0" on one of those
   * leaves the row with no focusable entry point at all: `.focus()` is a
   * silent no-op on a hidden element and focus drops to <body>, which is the
   * defect this theme has already shipped twice.
   */
  const setTabStop = (row, wanted) => {
    const all = Array.from(row.querySelectorAll(SWATCH));
    const reachable = visibleSwatches(row);
    const stop = reachable.includes(wanted) ? wanted : reachable[0] || all[0];
    all.forEach((el) => el.setAttribute('tabindex', el === stop ? '0' : '-1'));
    return stop;
  };

  const setSelection = (row, chosen) => {
    Array.from(row.querySelectorAll(SWATCH)).forEach((el) => {
      const isChosen = el === chosen;
      el.classList.toggle('fb-swatch-compact--selected', isChosen);
      el.setAttribute('aria-pressed', isChosen ? 'true' : 'false');
    });
    setTabStop(row, chosen);
  };

  /**
   * Writes one field, or returns it to the card's declared default.
   *
   * "Leave it alone when the colourway has none" reads correctly from an
   * unswapped card and wrongly from a swapped one: after a click on a
   * colourway with a full payload, leaving the photograph alone means the
   * PREVIOUS colourway's photograph sits under the new colourway's name. So
   * every field is applied from the default first and overlaid only by what
   * the clicked swatch actually supplies.
   */
  const applyPhoto = (card, photo, width) => {
    const img = card.querySelector('.fb-collection-card__photo');
    if (!img) return;

    /* URL AND WIDTH TRAVEL AS ONE PAYLOAD. Falling back independently would
       cap a colourway's photograph with the DEFAULT photograph's native
       width — claiming rungs the colourway's file cannot fill, which is the
       soft paint the cap exists to prevent. */
    const useDefault = !photo;
    const url = useDefault ? card.dataset.fbDefaultPhoto : photo;
    const native = useDefault ? card.dataset.fbDefaultPhotoWidth : width;
    if (!url) return;

    const built = buildSrcset(url, native);
    if (!built) return;

    img.srcset = built.srcset;
    img.src = built.src;
  };

  const applyPrice = (card, price) => {
    const value = card.querySelector('.fb-collection-card__price-value');
    const a11y = card.querySelector('.fb-collection-card__price-a11y');
    if (!value) return;

    value.textContent = price || card.dataset.fbDefaultPrice || '';
    // The default may be a range, which is spoken "to" rather than "-".
    if (a11y) a11y.textContent = price || card.dataset.fbDefaultPriceSpoken || '';
  };

  const applySoldOut = (card, isSoldOut) => {
    card.classList.toggle('fb-collection-card--sold-out', isSoldOut);
    const state = card.querySelector('.fb-collection-card__link-state');
    // The badge and the veil are sighted-only. This suffix is what reaches
    // Tab and the rotor's links list, which expose the link's name and
    // nothing else.
    if (state) state.textContent = isSoldOut ? ' (sold out)' : '';
  };

  const applyLink = (card, optionValueId) => {
    const link = card.querySelector('.fb-collection-card__link');
    if (!link) return;

    const base = card.dataset.fbDefaultHref;
    if (!base) return;

    let url;
    try {
      url = new URL(base, window.location.href);
    } catch {
      return;
    }

    // Shopify's documentation says one option-value id per option, in option
    // order. Observed behaviour — and base Horizon's own product-card path at
    // assets/variant-picker.js:274 — is that a colour alone resolves to the
    // first available size in that colour, which is what a grid card means.
    // Undocumented, so docs/plans carries a standing check; a regression here
    // opens the PDP on the default colourway with no error.
    if (optionValueId) {
      url.searchParams.set('option_values', optionValueId);
    } else {
      url.searchParams.delete('option_values');
    }

    link.setAttribute('href', url.href);

    // The `+N` chip opens the same colourway at the PDP's colour picker.
    const chip = card.querySelector('.fb-swatch-row__overflow-link');
    if (chip) {
      url.hash = 'fb-variant-picker';
      chip.setAttribute('href', url.href);
    }
  };

  const announce = (card, name, price) => {
    const status = card.querySelector('.fb-collection-card__status');
    if (!status) return;
    /* A reverted card shows the range, so it announces the range. */
    const spoken = price || card.dataset.fbDefaultPriceSpoken || '';
    status.textContent = [name, spoken].filter(Boolean).join(', ');
  };

  /** Re-dresses a card to one swatch's colourway. */
  const selectSwatch = (card, swatch) => {
    const row = swatch.closest(ROW);
    if (!row) return;

    const name = swatch.getAttribute('data-fb-swatch-tooltip') || '';
    const price = swatch.getAttribute('data-fb-price') || '';
    const photo = swatch.getAttribute('data-fb-photo') || '';
    const photoWidth = swatch.getAttribute('data-fb-photo-width') || '';
    const optionValueId = swatch.getAttribute('data-fb-option-value-id') || '';
    const soldOut = swatch.classList.contains('fb-swatch-compact--sold-out');

    setSelection(row, swatch);
    applyPhoto(card, photo, photoWidth);
    applyPrice(card, price);
    applySoldOut(card, soldOut);
    applyLink(card, optionValueId);

    const line = card.querySelector('.fb-collection-card__colorway');
    if (line) line.textContent = name;

    /* The visible line is a sibling of the anchor, not a descendant, so it
       never reaches the link's accessible name. Tab and the rotor's links
       list expose that name and nothing else — without this, every swapped
       card in a grid still announces the bare Style name. */
    const linkName = card.querySelector('.fb-collection-card__link-colorway');
    if (linkName) linkName.textContent = name ? `, ${name}` : '';

    card.classList.add('fb-collection-card--swatch-selected');
    announce(card, name, price);
  };

  /**
   * Returns a card to the state Liquid rendered.
   *
   * Used on a bfcache restore, where the browser hands back the swapped DOM
   * verbatim — including a price and an availability that may have moved
   * while the shopper was away. Dropping the `From` prefix turns a hedge into
   * a firm claim, and a firm claim that has gone stale reads as a bait price
   * rather than a stale one. Resetting needs no network; reconciling would.
   */
  const resetCard = (card) => {
    const row = card.querySelector(ROW);

    applyPhoto(card, '', '');
    applyPrice(card, '');
    applySoldOut(card, card.dataset.fbStyleSoldOut === 'true');
    applyLink(card, '');

    const line = card.querySelector('.fb-collection-card__colorway');
    if (line) line.textContent = card.dataset.fbDefaultColorway || '';

    const linkName = card.querySelector('.fb-collection-card__link-colorway');
    if (linkName) linkName.textContent = '';

    card.classList.remove('fb-collection-card--swatch-selected');

    const status = card.querySelector('.fb-collection-card__status');
    if (status) status.textContent = '';

    if (row) {
      const all = Array.from(row.querySelectorAll(SWATCH));
      const reachable = visibleSwatches(row);
      const first = reachable[0] || all[0];
      all.forEach((el) => {
        el.classList.remove('fb-swatch-compact--selected');
        el.setAttribute('aria-pressed', 'false');
        el.setAttribute('tabindex', el === first ? '0' : '-1');
      });
    }
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const swatch = target.closest(SWATCH);
    if (!swatch) return;

    // No card ancestor: the styleguide's standalone demo swatches. A no-op,
    // not an error.
    const card = swatch.closest(CARD);
    if (!card || !card.hasAttribute('data-fb-card')) return;

    selectSwatch(card, swatch);
  });

  document.addEventListener('keydown', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const swatch = target.closest(SWATCH);
    if (!swatch) return;

    const row = swatch.closest(ROW);
    const card = swatch.closest(CARD);
    if (!row || !card || !card.hasAttribute('data-fb-card')) return;

    // Traverse only what the viewport shows. Arrowing into a `display: none`
    // swatch cannot move focus, so the browser's fixup drops it to <body>.
    const reachable = visibleSwatches(row);
    const index = reachable.indexOf(swatch);
    if (index === -1) return;

    /* A MODIFIED ARROW IS NOT ROW TRAVERSAL. Alt+Left and Cmd+Left are
       browser Back; Ctrl/Alt+Arrow is word navigation. Matching on
       `event.key` alone and then calling preventDefault swallows all of
       them while a swatch has focus, and the shopper sees a page that will
       not go back, with no error. No modified combination is part of this
       row's contract, so they all fall through to the browser. */
    if (event.altKey || event.ctrlKey || event.metaKey) return;

    let next = null;
    switch (event.key) {
      case 'ArrowRight':
        next = reachable[(index + 1) % reachable.length];
        break;
      case 'ArrowLeft':
        next = reachable[(index - 1 + reachable.length) % reachable.length];
        break;
      case 'Home':
        next = reachable[0];
        break;
      case 'End':
        next = reachable[reachable.length - 1];
        break;
      default:
        // Enter and Space are left alone. A <button> already activates on
        // both, and handling them here would fire the swap twice.
        return;
    }

    if (!next) return;
    // Arrow keys inside the row must not also scroll the page.
    event.preventDefault();

    // Assign the tab stop BEFORE moving focus, never after. A focus call
    // aimed at an element whose state is still from the previous render is a
    // silent no-op, and the browser then drops focus to <body> — see
    // docs/solutions/theme-architecture/rescue-focus-after-disabling-not-before.md
    // Routed through the same writer the activation path uses, so the two
    // cannot drift apart.
    setTabStop(row, next);
    next.focus();
  });

  /**
   * A viewport change can hide the swatch holding the row's only tab stop,
   * leaving the row unreachable by Tab. Re-home it to the first visible one.
   */
  /* Which swatch last held focus, so a re-home can put focus back rather
     than only repairing the Tab order. `document.activeElement` is not
     usable inside the change handler: whether the browser's focus fixup
     runs before or after the matchMedia dispatch is unspecified, so by the
     time this reads it, focus may already be <body>. */
  let lastFocusedSwatch = null;
  document.addEventListener('focusin', (event) => {
    const el = event.target instanceof Element ? event.target.closest(SWATCH) : null;
    if (el) lastFocusedSwatch = el;
  });

  const rehomeTabStops = () => {
    document.querySelectorAll(`${CARD}[data-fb-card] ${ROW}`).forEach((row) => {
      const all = Array.from(row.querySelectorAll(SWATCH));
      if (!all.length) return;

      /* A widen can expose the chosen swatch again. Prefer it, so the ring
         and the tab stop do not sit on different swatches — the split
         setSelection exists to prevent. */
      const chosen = row.querySelector('.fb-swatch-compact--selected');
      const orphaned = lastFocusedSwatch && row.contains(lastFocusedSwatch) && lastFocusedSwatch.offsetParent === null;

      if (!orphaned && all.some((el) => el.getAttribute('tabindex') === '0' && el.offsetParent !== null)) {
        if (chosen && chosen.offsetParent !== null) setTabStop(row, chosen);
        return;
      }

      const stop = setTabStop(row, chosen && chosen.offsetParent !== null ? chosen : null);

      /* REPAIRING THE TAB ORDER IS HALF THE JOB. When the narrowing hid the
         swatch that held focus, the browser has already dropped focus to
         <body> — the defect this theme has shipped twice. Moving the zero
         does not bring it back; only a focus call does. */
      if (orphaned && stop) {
        stop.focus();
        lastFocusedSwatch = stop;
      }
    });
  };

  if (typeof MOBILE.addEventListener === 'function') {
    MOBILE.addEventListener('change', rehomeTabStops);
  }

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll(`${CARD}[data-fb-card]`).forEach(resetCard);
  });
})();
